import { useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { onActivate } from 'helpers/a11y';
import { placePopup } from 'helpers/placePopup';
import { useHoverPin } from './useHoverPin';
import styles from './FieldTooltip.module.css';

interface FieldTooltipProps {
  title: string;
  description: string;
  example?: string;
  note?: string;
}

// Hover or focus shows the help; a click pins it open so it stays while the pointer moves away.
// A second click, Escape or a click outside closes it.
export const FieldTooltip = ({ title, description, example, note }: FieldTooltipProps) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const { open, show, hideSoon, togglePin } = useHoverPin(triggerRef, popupRef);

  // The popup starts hidden (see the CSS). Measure it once it is on screen, put it where it fits, then show it.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !popupRef.current) return;
    const trigger = triggerRef.current.getBoundingClientRect();
    const popup = popupRef.current;
    const { top, left, maxHeight } = placePopup(trigger, popup.getBoundingClientRect(), { width: window.innerWidth, height: window.innerHeight });
    popup.style.setProperty('top', `${top}px`);
    popup.style.setProperty('left', `${left}px`);
    popup.style.setProperty('max-height', `${maxHeight}px`);
    popup.style.setProperty('visibility', 'visible');
  }, [open]);

  return (
    <span className={styles.wrap} onMouseEnter={show} onMouseLeave={hideSoon}>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        className={`${styles.trigger} ${open ? styles.triggerActive : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          togglePin();
        }}
        onKeyDown={onActivate(togglePin)}
        onFocus={show}
        onBlur={hideSoon}
        aria-label={t('FIELD.HELP')}
        aria-expanded={open}
      >
        <span className="material-icons">info</span>
      </span>

      {open && (
        <div ref={popupRef} className={styles.popup}>
          <div className={styles.header}>
            <span className={`material-icons ${styles.helpIcon}`}>help_outline</span>
            <span className={styles.headerTitle}>{title}</span>
          </div>

          <p className={styles.desc}>{description}</p>

          {example && (
            <div className={styles.exampleBlock}>
              <span className={styles.exampleLabel}>Example</span>
              <code className={styles.exampleCode}>{example}</code>
            </div>
          )}

          {note && (
            <div className={styles.noteBlock}>
              <span className={`material-icons ${styles.noteIcon}`}>warning_amber</span>
              <span>{note}</span>
            </div>
          )}
        </div>
      )}
    </span>
  );
};
